// components/hr/TeamMemberDetailDrawer.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Button,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/joy';
import {
  X as CloseOutlined,
  Envelope as EmailOutlined,
  Phone as PhoneOutlined,
  Briefcase as WorkOutlined,
  Buildings as BusinessOutlined,
  Calendar as CalendarTodayOutlined,
  CurrencyDollar as AttachMoneyOutlined,
  House as HomeOutlined,
  AddressBook as ContactEmergencyOutlined,
  PencilSimple as EditOutlined,
  FileText as DescriptionOutlined,
  UploadSimple as UploadOutlined,
  DownloadSimple as DownloadOutlined,
} from '@phosphor-icons/react';
import { useTeamMember } from '../../hooks/useHr';
import { apiClient } from '../../lib/api';
import { PersonnelReadiness } from './TeamWorkflowPanels';
import DocumentUploadModal from './DocumentUploadModal';
import type { TeamMemberStatus, DocumentType } from '../../types/hr';

interface TeamMemberDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  memberId: string | null;
  onEdit?: () => void;
}

const statusColors: Record<TeamMemberStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> = {
  pending: 'warning',
  onboarding: 'primary',
  active: 'success',
  inactive: 'neutral',
  terminated: 'danger',
};

const statusLabels: Record<TeamMemberStatus, string> = {
  pending: 'Pending',
  onboarding: 'Onboarding',
  active: 'Active',
  inactive: 'Inactive',
  terminated: 'Terminated',
};

// The personnel file is keyed on the person, not on the onboarding session it
// was collected in: sessions get cancelled and re-issued, the documents stay.
interface TeamMemberDocument {
  id: string;
  document_type: DocumentType;
  document_type_display: string;
  name: string;
  download_url: string | null;
  signed_contract: string | null;
  expires_at: string | null;
  created_at: string;
  is_hr_only: boolean;
}

const EXPIRING_SOON_DAYS = 30;

/** Null when the document does not expire. */
const getExpiryChip = (
  expiresAt: string | null
): { color: 'danger' | 'warning' | 'neutral'; label: string } | null => {
  if (!expiresAt) return null;

  const expiry = new Date(expiresAt);
  const daysLeft = Math.floor((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) {
    return { color: 'danger', label: `Expired ${expiry.toLocaleDateString()}` };
  }
  if (daysLeft <= EXPIRING_SOON_DAYS) {
    return {
      color: 'warning',
      label: daysLeft === 0 ? 'Expires today' : `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`,
    };
  }
  return { color: 'neutral', label: `Expires ${expiry.toLocaleDateString()}` };
};

export const TeamMemberDetailDrawer: React.FC<TeamMemberDetailDrawerProps> = ({
  open,
  onClose,
  memberId,
  onEdit,
}) => {
  const { data: member, isLoading, error } = useTeamMember(memberId || '');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [visibilityBusy, setVisibilityBusy] = useState<string | null>(null);
  const [visibilityError, setVisibilityError] = useState('');

  const {
    data: documents,
    isLoading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments,
  } = useQuery({
    queryKey: ['team-member-documents', memberId],
    queryFn: async () => {
      const response = await apiClient.get<{ results: TeamMemberDocument[] }>(
        `/api/hr/onboarding-documents/?team_member=${memberId}&page_size=100`
      );
      return response.data.results;
    },
    enabled: open && !!memberId,
  });

  if (!memberId) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        content: {
          sx: {
            width: { xs: '100%', sm: 450 },
            p: 0,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography level="title-lg">Team Member Details</Typography>
        <IconButton variant="plain" onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error || !member ? (
          <Typography color="danger" sx={{ textAlign: 'center', py: 4 }}>
            Failed to load team member details
          </Typography>
        ) : (
          <>
            {/* Profile Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Avatar
                src={member.profile_photo || undefined}
                alt={member.full_name}
                sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
              >
                {member.first_name[0]}{member.last_name[0]}
              </Avatar>
              <Typography level="h4">{member.full_name}</Typography>
              <Typography level="body-sm" color="neutral">
                {member.job_title || 'No job title'}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Chip size="sm" color={statusColors[member.status]} variant="soft">
                  {statusLabels[member.status]}
                </Chip>
                <Chip size="sm" variant="soft">
                  {member.member_type === 'w2' ? 'W-2 Employee' : '1099 Contractor'}
                </Chip>
              </Box>
            </Box>

            <PersonnelReadiness memberId={member.id} />
            {/* Onboarding Progress */}
            {member.current_onboarding_session && (
              <Card variant="soft" color="primary" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Onboarding Progress
                  </Typography>
                  <LinearProgress
                    determinate
                    value={member.current_onboarding_session.completion_percentage}
                    sx={{ mb: 1 }}
                  />
                  <Typography level="body-xs">
                    {member.current_onboarding_session.completion_percentage}% Complete
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Contact Information</Typography>
            <List sx={{ mb: 2 }}>
              <ListItem>
                <ListItemDecorator><EmailOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Email</Typography>
                  <Typography level="body-sm">{member.email}</Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator><PhoneOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Phone</Typography>
                  <Typography level="body-sm">{member.phone || 'Not provided'}</Typography>
                </ListItemContent>
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Employment Details */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Employment Details</Typography>
            <List sx={{ mb: 2 }}>
              <ListItem>
                <ListItemDecorator><WorkOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Job Title</Typography>
                  <Typography level="body-sm">{member.job_title || 'Not set'}</Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator><BusinessOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Department</Typography>
                  <Typography level="body-sm">{member.department || 'Not set'}</Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator><CalendarTodayOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Hire Date</Typography>
                  <Typography level="body-sm">
                    {member.hire_date ? new Date(member.hire_date).toLocaleDateString() : 'Not set'}
                  </Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator><CalendarTodayOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Start Date</Typography>
                  <Typography level="body-sm">
                    {member.start_date ? new Date(member.start_date).toLocaleDateString() : 'Not set'}
                  </Typography>
                </ListItemContent>
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Compensation */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Compensation</Typography>
            <List sx={{ mb: 2 }}>
              {member.hourly_rate && (
                <ListItem>
                  <ListItemDecorator><AttachMoneyOutlined /></ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-xs" color="neutral">Hourly Rate</Typography>
                    <Typography level="body-sm">${member.hourly_rate}/hr</Typography>
                  </ListItemContent>
                </ListItem>
              )}
              {member.salary && (
                <ListItem>
                  <ListItemDecorator><AttachMoneyOutlined /></ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-xs" color="neutral">Salary</Typography>
                    <Typography level="body-sm">${member.salary.toLocaleString()}/year</Typography>
                  </ListItemContent>
                </ListItem>
              )}
              {member.commission_rate && (
                <ListItem>
                  <ListItemDecorator><AttachMoneyOutlined /></ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-xs" color="neutral">Commission Rate</Typography>
                    <Typography level="body-sm">{member.commission_rate}%</Typography>
                  </ListItemContent>
                </ListItem>
              )}
              {!member.hourly_rate && !member.salary && !member.commission_rate && (
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-sm" color="neutral">No compensation details set</Typography>
                  </ListItemContent>
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Address */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Address</Typography>
            <List sx={{ mb: 2 }}>
              <ListItem>
                <ListItemDecorator><HomeOutlined /></ListItemDecorator>
                <ListItemContent>
                  {member.address_line_1 ? (
                    <>
                      <Typography level="body-sm">{member.address_line_1}</Typography>
                      {member.address_line_2 && (
                        <Typography level="body-sm">{member.address_line_2}</Typography>
                      )}
                      <Typography level="body-sm">
                        {member.city}, {member.state} {member.zip_code}
                      </Typography>
                    </>
                  ) : (
                    <Typography level="body-sm" color="neutral">No address provided</Typography>
                  )}
                </ListItemContent>
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Emergency Contact */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Emergency Contact</Typography>
            <List sx={{ mb: 2 }}>
              <ListItem>
                <ListItemDecorator><ContactEmergencyOutlined /></ListItemDecorator>
                <ListItemContent>
                  {member.emergency_contact_name ? (
                    <>
                      <Typography level="body-sm">{member.emergency_contact_name}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {member.emergency_contact_relationship} - {member.emergency_contact_phone}
                      </Typography>
                    </>
                  ) : (
                    <Typography level="body-sm" color="neutral">No emergency contact provided</Typography>
                  )}
                </ListItemContent>
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Documents */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography level="title-sm">Documents</Typography>
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<UploadOutlined />}
                onClick={() => setUploadOpen(true)}
              >
                Upload
              </Button>
            </Box>
            {visibilityError && <Typography color="danger" role="alert">{visibilityError}</Typography>}
            {documentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size="sm" />
              </Box>
            ) : documentsError ? (
              <Typography level="body-sm" color="danger" sx={{ mb: 2 }}>
                Failed to load documents
              </Typography>
            ) : !documents || documents.length === 0 ? (
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                No documents on file
              </Typography>
            ) : (
              <List sx={{ mb: 2 }}>
                {documents.map((doc) => {
                  const expiry = getExpiryChip(doc.expires_at);
                  return (
                    <ListItem
                      key={doc.id}
                      endAction={
                        doc.download_url ? (
                          <IconButton
                            variant="plain"
                            color="neutral"
                            size="sm"
                            component="a"
                            href={doc.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Download ${doc.name}`}
                          >
                            <DownloadOutlined />
                          </IconButton>
                        ) : undefined
                      }
                    >
                      <ListItemDecorator><DescriptionOutlined /></ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm">{doc.name}</Typography>
                        <Typography level="body-xs" color="neutral">
                          {doc.document_type_display} - {doc.signed_contract ? 'Signed' : 'Uploaded'}{' '}
                          {new Date(doc.created_at).toLocaleDateString()}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography level="body-xs">{doc.is_hr_only ? 'HR-only' : 'Visible in My Employee File'}</Typography>
                          <Button size="sm" variant="plain" disabled={!!visibilityBusy} loading={visibilityBusy === doc.id} onClick={async () => {
                            setVisibilityBusy(doc.id); setVisibilityError('');
                            try {
                              await apiClient.patch(`/api/hr/onboarding-documents/${doc.id}/employee-visibility/`, { is_hr_only: !doc.is_hr_only });
                              await refetchDocuments();
                            } catch { setVisibilityError('Unable to update document visibility. Please try again.'); }
                            finally { setVisibilityBusy(null); }
                          }}>{doc.is_hr_only ? 'Share with employee' : 'Make HR-only'}</Button>
                        </Box>
                        {expiry && (
                          <Chip size="sm" variant="soft" color={expiry.color} sx={{ mt: 0.5 }}>
                            {expiry.label}
                          </Chip>
                        )}
                      </ListItemContent>
                    </ListItem>
                  );
                })}
              </List>
            )}

            <DocumentUploadModal
              open={uploadOpen}
              onClose={() => setUploadOpen(false)}
              memberId={member.id}
              memberName={member.full_name}
              sessionId={member.current_onboarding_session?.id}
              onUploaded={() => refetchDocuments()}
            />

            {/* Notes */}
            {member.notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography level="title-sm" sx={{ mb: 1 }}>Notes</Typography>
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                  {member.notes}
                </Typography>
              </>
            )}
          </>
        )}
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          color="neutral"
          fullWidth
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          startDecorator={<EditOutlined />}
          fullWidth
          onClick={onEdit}
        >
          Edit
        </Button>
      </Box>
    </Drawer>
  );
};

export default TeamMemberDetailDrawer;
